import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { AlertTriangle, Building2, Users, BookOpen, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-sm text-slate-500">{label}</p>
      <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
    </div>
    <p className="font-data text-3xl font-semibold text-slate-950 mt-3" data-testid={`${testid}-value`}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [semesterFilter, setSemesterFilter] = useState("all");

  const { data: overview } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => (await api.get("/admin/overview")).data,
  });
  const { data: semesters = [] } = useQuery({
    queryKey: ["admin-semesters"],
    queryFn: async () => (await api.get("/admin/semesters")).data,
  });
  const { data: lowList = [] } = useQuery({
    queryKey: ["admin-low-attendance"],
    queryFn: async () => (await api.get("/admin/low-attendance")).data,
  });
  const { data: trends } = useQuery({
    queryKey: ["admin-trends", semesterFilter],
    queryFn: async () => (await api.get("/admin/trends", { params: semesterFilter === "all" ? {} : { semester_id: semesterFilter } })).data,
  });

  return (
    <Layout title="Admin Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-testid="admin-stats-grid">
        <StatCard label="Departments" value={overview?.departments ?? "—"} icon={Building2} testid="stat-departments" />
        <StatCard label="Teachers" value={overview?.teachers ?? "—"} icon={Users} testid="stat-teachers" />
        <StatCard label="Students" value={overview?.students ?? "—"} icon={Users} testid="stat-students" />
        <StatCard label="Subjects" value={overview?.subjects ?? "—"} icon={BookOpen} testid="stat-subjects" />
        <StatCard label="Sessions Marked" value={overview?.sessions ?? "—"} icon={CalendarCheck} testid="stat-sessions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-8">
        <div className="lg:col-span-3 space-y-8">
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="trends-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Attendance Trends</h2>
              <div className="w-56">
                <Select value={semesterFilter} onValueChange={setSemesterFilter}>
                  <SelectTrigger data-testid="trends-semester-filter"><SelectValue placeholder="All semesters" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="trends-semester-all">All semesters</SelectItem>
                    {semesters.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`trends-semester-${s.id}`}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-slate-500 mb-4">Subject-wise average attendance</p>
                {trends?.subject_wise?.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={trends.subject_wise} data-testid="subject-bar-chart">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="subject" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip formatter={(v) => [`${v}%`, "Attendance"]} />
                      <Bar dataKey="percentage" fill="#0f172a" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] border border-dashed border-slate-300 rounded-md flex items-center justify-center" data-testid="subject-chart-empty">
                    <p className="text-sm text-slate-400">No attendance data yet</p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-4">Monthly attendance trend</p>
                {trends?.monthly?.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={trends.monthly} data-testid="monthly-line-chart">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip formatter={(v) => [`${v}%`, "Attendance"]} />
                      <Line type="monotone" dataKey="percentage" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] border border-dashed border-slate-300 rounded-md flex items-center justify-center" data-testid="monthly-chart-empty">
                    <p className="text-sm text-slate-400">No attendance data yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 lg:row-span-2" data-testid="low-attendance-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={1.75} />
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Low Attendance</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Students below their department's attendance threshold. Daily overall-attendance warnings go out automatically (in-app + email) until they recover.</p>
          {lowList.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md p-6 text-center" data-testid="low-attendance-empty">
              <p className="text-sm text-slate-400">No students below threshold.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="low-attendance-list">
              {lowList.map((r) => (
                <div key={`${r.student_id}-${r.subject_id}`} data-testid={`low-attendance-${r.student_id}-${r.subject_id}`}
                  className="border border-red-200 bg-red-50 rounded-md px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-950">{r.name}</p>
                    <span className="font-data text-sm font-semibold text-red-700">{r.percentage}%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{r.roll_number} · {r.subject_name}</p>
                  <p className="text-xs text-slate-400 font-data">{r.present}/{r.total} classes</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
